import { AlertTriangleIcon, InboxIcon, RotateCcwIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AsortiCell } from '@app/components/asorti-grid'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { TableCell, TableRow } from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { formatAmount, formatQty, percentText } from './format'
import type {
  PurchaseCurrencyBreakdown,
  PurchaseReceiptStatus,
  PurchaseSizeCell,
  PurchaseStatus,
} from './api'

/* ── Badges ───────────────────────────────────────────────────────────────── */

const RECEIPT_LABEL: Record<PurchaseReceiptStatus, string> = {
  pending: 'Bekliyor',
  partial: 'Kısmi geldi',
  complete: 'Tamamlandı',
}

const RECEIPT_TONE = {
  pending: 'neutral',
  partial: 'info',
  complete: 'ok',
} as const

/**
 * Goods-receipt state. Computed from the Si_GCHar ledger, never from `Durum`:
 * 97 closed purchase orders still carry a remaining quantity.
 */
export function ReceiptBadge({
  status,
  overdue = false,
}: {
  status: PurchaseReceiptStatus
  overdue?: boolean
}) {
  if (overdue && status !== 'complete') {
    return <Badge tone="warn">{RECEIPT_LABEL[status]} · gecikti</Badge>
  }
  return <Badge tone={RECEIPT_TONE[status]}>{RECEIPT_LABEL[status]}</Badge>
}

export function StatusBadge({ status }: { status: PurchaseStatus }) {
  return status === 'closed' ? (
    <Badge tone="neutral" title="Korgun Durum = '*' (kapatılmış kayıt)">
      Kapalı
    </Badge>
  ) : (
    <Badge tone="accent">Açık</Badge>
  )
}

/* ── Receipt bar ──────────────────────────────────────────────────────────── */

export interface ReceiptBarProps {
  percent: number
  status: PurchaseReceiptStatus
  overdue?: boolean
  /** Show the percentage in mono next to the bar. */
  showLabel?: boolean
  className?: string
}

/**
 * Thin progress bar: received / ordered. Kordovan is reserved for primary
 * actions across the app, so the fill uses the semantic status colours.
 */
export function ReceiptBar({
  percent,
  status,
  overdue = false,
  showLabel = true,
  className,
}: ReceiptBarProps) {
  const fill =
    status === 'complete'
      ? 'bg-zeytin'
      : overdue
        ? 'bg-hardal'
        : status === 'partial'
          ? 'bg-celik'
          : 'bg-ink-muted/35'

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className="h-1 min-w-10 flex-1 overflow-hidden rounded-xs bg-hairline"
        role="img"
        aria-label={`Mal kabul ${percentText(percent)}`}
      >
        <div
          className={cn('h-full', fill)}
          style={{ width: `${Math.max(percent, percent > 0 ? 4 : 0)}%` }}
        />
      </div>
      {showLabel ? (
        <span
          aria-hidden
          className={cn(
            'w-9 shrink-0 text-right font-mono text-micro tnum',
            status === 'complete' ? 'text-zeytin' : overdue ? 'text-hardal' : 'text-ink-muted',
          )}
        >
          {/* 6 pairs received on a 1,330-pair order rounds to %0 — don't hide that. */}
          {percent === 0 && status === 'partial' ? '<%1' : percentText(percent)}
        </span>
      ) : null}
    </div>
  )
}

/* ── Delivery date ────────────────────────────────────────────────────────── */

/**
 * `TeslimTar` is set on only 66 of 563 purchase orders, so most rows render a
 * plain em dash here. That is deliberate: inventing a due date would make the
 * "geciken" chip meaningless.
 */
export function DeliveryDateCell({
  dateText,
  overdueDays,
}: {
  dateText: string
  overdueDays: number | null
}) {
  if (overdueDays === null) {
    return <span className="font-mono tnum text-ink-muted">{dateText}</span>
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="font-mono tnum font-medium text-hardal">{dateText}</span>
      <span className="rounded-xs bg-hardal/10 px-1 font-mono text-micro tnum text-hardal">
        +{overdueDays} g
      </span>
    </span>
  )
}

/* ── Money ────────────────────────────────────────────────────────────────── */

/**
 * Per-currency amounts stacked, never summed. Empty (or all-zero) input renders
 * as an em dash — a purchase order without prices is the normal case here.
 */
export function AmountStack({
  amounts,
  className,
}: {
  amounts: readonly PurchaseCurrencyBreakdown[]
  className?: string
}) {
  const priced = amounts.filter((a) => a.amount !== 0)
  if (priced.length === 0) {
    return <span className={cn('text-ink-muted/60', className)}>—</span>
  }
  return (
    <span className={cn('flex flex-col items-end leading-tight', className)}>
      {priced.map((a) => (
        <span key={a.currency} className="font-mono tnum">
          {formatAmount(a.amount, a.currency)}
        </span>
      ))}
    </span>
  )
}

/* ── Size strip ───────────────────────────────────────────────────────────── */

/**
 * Turns a line's size breakdown into `<AsortiGrid>` cells.
 *
 * Soles, heels and insoles are ordered per shoe size, so this strip is as
 * meaningful on a purchase order as it is on a sales order: olive = fully
 * received, steel = partly, neutral = nothing arrived yet.
 */
export function toSizeCells(sizes: readonly PurchaseSizeCell[]): AsortiCell[] {
  return sizes.map((s) => ({
    size: s.size,
    qty: s.ordered,
    status: s.ordered > 0 && s.kalan === 0 ? 'ok' : s.received > 0 ? 'info' : 'neutral',
  }))
}

/* ── Small parts ──────────────────────────────────────────────────────────── */

export function Field({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow">{label}</dt>
      <dd className={cn('mt-0.5 truncate text-data text-ink', mono && 'font-mono tnum')}>{value}</dd>
    </div>
  )
}

export function MiniStat({
  label,
  value,
  highlighted = false,
}: {
  label: string
  value: string
  highlighted?: boolean
}) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 font-mono text-data tnum',
          highlighted ? 'font-semibold text-ink' : 'text-ink-muted',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/** Ordered / received / remaining, the three numbers every purchase row is about. */
export function QtyTriple({
  ordered,
  received,
  kalan,
}: {
  ordered: number
  received: number
  kalan: number
}) {
  return (
    <dl className="grid grid-cols-3 gap-2 text-center">
      <MiniStat label="Sipariş" value={formatQty(ordered)} />
      <MiniStat label="Gelen" value={formatQty(received)} />
      <MiniStat label="Kalan" value={formatQty(kalan)} highlighted={kalan > 0} />
    </dl>
  )
}

/* ── State screens ────────────────────────────────────────────────────────── */

export function TableSkeleton({ rows = 8, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i} aria-hidden>
          {Array.from({ length: cols }, (__, j) => (
            <TableCell key={j}>
              <span
                className="block h-2.5 animate-pulse rounded-xs bg-hairline"
                style={{ width: `${[70, 55, 90, 45, 60, 50, 40, 65][j % 8]}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <InboxIcon className="size-5 text-ink-muted/60" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/**
 * Failed load. `action` replaces the retry button for errors retrying cannot
 * fix — a 404 is the server's final answer, so the detail screen hands in a
 * "back to the list" link instead of a button that would fail again.
 */
export function ErrorState({
  title = 'Veriler yüklenemedi',
  message,
  onRetry,
  action,
}: {
  title?: string
  message: string
  onRetry?: () => void
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <AlertTriangleIcon className="size-5 text-kordovan" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{message}</p>
      {action ? (
        <div className="mt-1">{action}</div>
      ) : onRetry ? (
        <Button variant="outline" className="mt-1" onClick={onRetry}>
          <RotateCcwIcon aria-hidden />
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}
